import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircle2,
  Droplets,
  Pencil,
  Plus,
  Save,
  Tag,
  Trash2,
  X,
} from "lucide-react";

import {
  createCategory,
  createSampleType,
  deleteCategory,
  deleteSampleType,
  fetchSampleTypes,
  fetchTestCategories,
  updateCategory,
  updateSampleType,
} from "../../store/slices/testSlice";

import {
  createTubeType,
  deleteTubeType,
  fetchTubeTypes,
  updateTubeType,
} from "../../store/slices/tubeTypeSlice";

import {
  createSampleTypeParameter,
  createTemplateRange,
  deleteSampleTypeParameter,
  deleteTemplateRange,
  fetchSampleTypeParameters,
  fetchSampleTypeTubeTypes,
  fetchTemplateRanges,
  saveSampleTypeTubeTypes,
  updateSampleTypeParameter,
} from "../../store/slices/sampleTypeConfigSlice";

const cx = (...xs) => xs.filter(Boolean).join(" ");
const EMPTY_ARR = [];

const Card = ({ children, className = "" }) => (
  <div className={"rounded-xl border border-slate-200 bg-white " + className}>
    {children}
  </div>
);

const CardHeader = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-4">
    <div className="min-w-0">
      <div className="text-base font-semibold text-slate-900">{title}</div>
      {subtitle ? <div className="mt-1 text-sm text-slate-600">{subtitle}</div> : null}
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <div className="mb-1 text-xs font-medium text-slate-700">{label}</div>
    {children}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={cx(
      "h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200",
      props.className
    )}
  />
);

const TextArea = (props) => (
  <textarea
    {...props}
    className={cx(
      "min-h-[90px] w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200",
      props.className
    )}
  />
);

const Select = (props) => (
  <select
    {...props}
    className={cx(
      "h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200",
      props.className
    )}
  />
);

const PrimaryButton = ({ children, className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60",
      className
    )}
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60",
      className
    )}
  >
    {children}
  </button>
);

const DangerButton = ({ children, className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60",
      className
    )}
  >
    {children}
  </button>
);

const Badge = ({ tone = "neutral", children }) => {
  const cls =
    tone === "success"
      ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
      : tone === "danger"
        ? "bg-rose-50 text-rose-800 ring-1 ring-rose-200"
        : "bg-slate-50 text-slate-700 ring-1 ring-slate-200";
  return (
    <span className={"inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium " + cls}>
      {children}
    </span>
  );
};

const Tabs = ({ tabs, activeId, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {tabs.map((t) => (
      <button
        key={t.id}
        type="button"
        onClick={() => onChange(t.id)}
        className={cx(
          "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium",
          activeId === t.id
            ? "border-indigo-200 bg-indigo-50 text-indigo-700"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        )}
      >
        {t.Icon ? <t.Icon className="h-4 w-4" /> : null}
        {t.label}
      </button>
    ))}
  </div>
);

const Modal = ({ open, title, children, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-label="Close"
      />
      <div className="relative w-full max-w-xl rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <div className="text-base font-semibold text-slate-900">{title}</div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const DATA_TYPES = [
  { value: "numeric", label: "Numeric" },
  { value: "text", label: "Text" },
];

const LaboratoryConfiguration = () => {
  const dispatch = useDispatch();

  const { testCategories = [], sampleTypes = [], loading } = useSelector((s) => s.tests);
  const tubeTypesRaw = useSelector((s) => s.tubeTypes.items);
  const tubeTypes = tubeTypesRaw ?? EMPTY_ARR;
  const config = useSelector((s) => s.sampleTypeConfig);

  const [tab, setTab] = React.useState("categories");

  // Categories modal state
  const [categoryModalOpen, setCategoryModalOpen] = React.useState(false);
  const [categoryEditingId, setCategoryEditingId] = React.useState(null);
  const [categoryForm, setCategoryForm] = React.useState({
    name: "",
    description: "",
    is_active: true,
  });

  // Create sample type modal state
  const [sampleTypeCreateOpen, setSampleTypeCreateOpen] = React.useState(false);
  const [sampleTypeCreateForm, setSampleTypeCreateForm] = React.useState({
    name: "",
    description: "",
    is_active: true,
  });

  // Tube types modal state
  const [tubeTypeModalOpen, setTubeTypeModalOpen] = React.useState(false);
  const [tubeTypeEditingId, setTubeTypeEditingId] = React.useState(null);
  const [tubeTypeForm, setTubeTypeForm] = React.useState({
    name: "",
    description: "",
    is_active: true,
  });
  const [tubeTypeCreateLinkSampleTypeId, setTubeTypeCreateLinkSampleTypeId] = React.useState(null);

  // Confirm dialog state (shared)
  const [confirmDialog, setConfirmDialog] = React.useState({
    open: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    confirmTone: "danger",
  });
  const confirmDialogActionRef = React.useRef(null);
  const confirmDialogCancelRef = React.useRef(null);
  const [confirmDialogBusy, setConfirmDialogBusy] = React.useState(false);

  const openConfirmDialog = React.useCallback(
    ({ title, message, confirmText, confirmTone, onConfirm, onCancel }) => {
      confirmDialogActionRef.current = onConfirm || null;
      confirmDialogCancelRef.current = onCancel || null;
      setConfirmDialog({
        open: true,
        title: title || "Confirm",
        message: message || "Are you sure?",
        confirmText: confirmText || "Confirm",
        confirmTone: confirmTone || "danger",
      });
    },
    []
  );

  const closeConfirmDialog = React.useCallback(
    (invokeCancel = true) => {
      if (confirmDialogBusy) return;
      const onCancel = confirmDialogCancelRef.current;
      if (invokeCancel && typeof onCancel === "function") {
        try {
          onCancel();
        } catch {
          // ignore
        }
      }
      confirmDialogActionRef.current = null;
      confirmDialogCancelRef.current = null;
      setConfirmDialog((p) => ({ ...p, open: false }));
    },
    [confirmDialogBusy]
  );

  const confirmAsync = React.useCallback(
    ({ title, message, confirmText, confirmTone }) =>
      new Promise((resolve) => {
        openConfirmDialog({
          title,
          message,
          confirmText,
          confirmTone,
          onConfirm: async () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    [openConfirmDialog]
  );

  // Sample type manage
  const [managedSampleTypeId, setManagedSampleTypeId] = React.useState(null);
  const [sampleTypeSection, setSampleTypeSection] = React.useState("overview");
  const [managedParameterId, setManagedParameterId] = React.useState(null);

  // Sample type edit draft
  const [sampleTypeDraft, setSampleTypeDraft] = React.useState({
    name: "",
    description: "",
    is_active: true,
  });

  // Parameter add/edit
  const [parameterForm, setParameterForm] = React.useState({
    name: "",
    data_type: "numeric",
    unit: "",
  });
  const [editingParameterId, setEditingParameterId] = React.useState(null);
  const [parameterEditDraft, setParameterEditDraft] = React.useState({
    name: "",
    data_type: "numeric",
    unit: "",
    is_active: true,
  });

  // Range form (structured, no JSON)
  const [rangeForm, setRangeForm] = React.useState({
    gender: "both",
    pregnancy_status: "both",
    age_unit: "years",
    min_age: "",
    max_age: "",
    unit: "",
    min_value: "",
    max_value: "",
    critical_low: "",
    critical_high: "",
    conditions: "",
  });

  React.useEffect(() => {
    dispatch(fetchTestCategories());
    dispatch(fetchSampleTypes(false));
    dispatch(fetchTubeTypes(false));
  }, [dispatch]);

  // Preload metadata for sample type list (tube links + parameter counts)
  React.useEffect(() => {
    (sampleTypes || []).forEach((st) => {
      dispatch(fetchSampleTypeTubeTypes(st.id));
      dispatch(fetchSampleTypeParameters(st.id));
    });
  }, [dispatch, sampleTypes]);

  const categoriesSorted = React.useMemo(() => {
    return [...(testCategories || [])].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""))
    );
  }, [testCategories]);

  const sampleTypesSorted = React.useMemo(() => {
    return [...(sampleTypes || [])].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""))
    );
  }, [sampleTypes]);

  const tubeTypesSorted = React.useMemo(() => {
    return [...(tubeTypes || [])].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""))
    );
  }, [tubeTypes]);

  const managedSampleType = React.useMemo(() => {
    return (sampleTypes || []).find((st) => String(st.id) === String(managedSampleTypeId)) || null;
  }, [sampleTypes, managedSampleTypeId]);

  const managedTubeTypeIds =
    (managedSampleTypeId &&
      config.tubeTypeIdsBySampleTypeId[managedSampleTypeId]) ||
    [];

  const managedParameters = React.useMemo(() => {
    if (!managedSampleTypeId) return EMPTY_ARR;
    return config.parametersBySampleTypeId[managedSampleTypeId] || EMPTY_ARR;
  }, [managedSampleTypeId, config.parametersBySampleTypeId]);

  const selectedParam = React.useMemo(() => {
    if (!managedParameterId) return null;
    return managedParameters.find((p) => String(p.id) === String(managedParameterId)) || null;
  }, [managedParameterId, managedParameters]);

  const isTextParameter = String(selectedParam?.data_type || "numeric").toLowerCase() === "text";

  const selectedRanges =
    (managedParameterId && config.rangesByParameterId[managedParameterId]) || [];
  const hasTextRangeRows = selectedRanges.some(
    (r) => String(r?.text_range ?? "").trim().length > 0 || String(r?.remarks ?? "").trim().length > 0
  );
  const showTextRangeColumns = isTextParameter || hasTextRangeRows;

  const openCreateCategory = () => {
    setCategoryEditingId(null);
    setCategoryForm({ name: "", description: "", is_active: true });
    setCategoryModalOpen(true);
  };

  const openEditCategory = (c) => {
    setCategoryEditingId(c.id);
    setCategoryForm({
      name: c.name || "",
      description: c.description || "",
      is_active: c.is_active === 0 ? false : true,
    });
    setCategoryModalOpen(true);
  };

  const saveCategory = async () => {
    const name = String(categoryForm.name || "").trim();
    if (!name) return;

    if (categoryEditingId) {
      await dispatch(
        updateCategory({
          id: categoryEditingId,
          data: {
            name,
            description: categoryForm.description || null,
            is_active: categoryForm.is_active ? 1 : 0,
          },
        })
      );
    } else {
      await dispatch(
        createCategory({
          name,
          description: categoryForm.description || null,
          is_active: categoryForm.is_active ? 1 : 0,
        })
      );
    }

    setCategoryModalOpen(false);
  };

  const deleteCategoryRow = async (c) => {
    const ok = await confirmAsync({
      title: "Delete category",
      message: `Delete category '${c?.name || c?.id}'? This cannot be undone.`,
      confirmText: "Delete",
      confirmTone: "danger",
    });
    if (!ok) return;
    await dispatch(deleteCategory(c.id));
  };

  const disableCategory = async (c) => {
    await dispatch(
      updateCategory({
        id: c.id,
        data: { is_active: 0 },
      })
    );
  };

  const enableCategory = async (c) => {
    await dispatch(
      updateCategory({
        id: c.id,
        data: { is_active: 1 },
      })
    );
  };

  const openManageSampleType = (st) => {
    setManagedSampleTypeId(st.id);
    setSampleTypeSection("overview");
    setManagedParameterId(null);
    setSampleTypeDraft({
      name: st.name || "",
      description: st.description || "",
      is_active: st.is_active === 0 ? false : true,
    });

    dispatch(fetchSampleTypeTubeTypes(st.id));
    dispatch(fetchSampleTypeParameters(st.id));
  };

  const backToSampleTypes = () => {
    setManagedSampleTypeId(null);
    setManagedParameterId(null);
    setSampleTypeSection("overview");
  };

  const saveSampleTypeOverview = async () => {
    if (!managedSampleTypeId) return;

    const name = String(sampleTypeDraft.name || "").trim();
    if (!name) return;

    await dispatch(
      updateSampleType({
        id: managedSampleTypeId,
        data: {
          name,
          description: sampleTypeDraft.description || null,
          is_active: sampleTypeDraft.is_active ? 1 : 0,
        },
      })
    );
  };

  const openCreateSampleType = () => {
    setSampleTypeCreateForm({ name: "", description: "", is_active: true });
    setSampleTypeCreateOpen(true);
  };

  const saveNewSampleType = async () => {
    const name = String(sampleTypeCreateForm.name || "").trim();
    if (!name) return;

    await dispatch(
      createSampleType({
        name,
        description: sampleTypeCreateForm.description || null,
        is_active: sampleTypeCreateForm.is_active ? 1 : 0,
      })
    );
    setSampleTypeCreateOpen(false);
  };

  const deleteSampleTypeRow = async (st) => {
    const ok = await confirmAsync({
      title: "Delete sample type",
      message: `Delete sample type '${st?.name || st?.id}'? This cannot be undone.`,
      confirmText: "Delete",
      confirmTone: "danger",
    });
    if (!ok) return;
    await dispatch(deleteSampleType(st.id));
    if (String(managedSampleTypeId) === String(st.id)) backToSampleTypes();
  };

  const disableSampleTypeRow = async (st) => {
    await dispatch(updateSampleType({ id: st.id, data: { is_active: 0 } }));
  };

  const enableSampleTypeRow = async (st) => {
    await dispatch(updateSampleType({ id: st.id, data: { is_active: 1 } }));
  };

  const openCreateTubeType = ({ linkToSampleTypeId } = {}) => {
    setTubeTypeEditingId(null);
    setTubeTypeForm({ name: "", description: "", is_active: true });
    setTubeTypeCreateLinkSampleTypeId(linkToSampleTypeId ?? null);
    setTubeTypeModalOpen(true);
  };

  const openEditTubeType = (t) => {
    setTubeTypeEditingId(t.id);
    setTubeTypeForm({
      name: t.name || "",
      description: t.description || "",
      is_active: t.is_active === 0 ? false : true,
    });
    setTubeTypeCreateLinkSampleTypeId(null);
    setTubeTypeModalOpen(true);
  };

  const saveTubeType = async () => {
    const name = String(tubeTypeForm.name || "").trim();
    if (!name) return;

    if (tubeTypeEditingId) {
      await dispatch(
        updateTubeType({
          id: tubeTypeEditingId,
          data: {
            name,
            description: tubeTypeForm.description || null,
            is_active: tubeTypeForm.is_active ? 1 : 0,
          },
        })
      );
      setTubeTypeModalOpen(false);
      return;
    }

    const action = await dispatch(
      createTubeType({
        name,
        description: tubeTypeForm.description || null,
        is_active: tubeTypeForm.is_active ? 1 : 0,
      })
    );

    const createdId = action?.payload?.id;
    const linkSampleTypeId = tubeTypeCreateLinkSampleTypeId;

    setTubeTypeModalOpen(false);
    setTubeTypeCreateLinkSampleTypeId(null);

    if (createdId && linkSampleTypeId) {
      const currentIds = config.tubeTypeIdsBySampleTypeId[linkSampleTypeId] || [];
      const next = currentIds.includes(createdId) ? currentIds : [...currentIds, createdId];
      await dispatch(saveSampleTypeTubeTypes({ sampleTypeId: linkSampleTypeId, tubeTypeIds: next }));
    }
  };

  const deleteTubeTypeRow = async (t) => {
    const ok = await confirmAsync({
      title: "Delete tube type",
      message: `Delete tube type '${t?.name || t?.id}'? This cannot be undone.`,
      confirmText: "Delete",
      confirmTone: "danger",
    });
    if (!ok) return;
    await dispatch(deleteTubeType(t.id));
  };

  const disableTubeTypeRow = async (t) => {
    await dispatch(updateTubeType({ id: t.id, data: { is_active: 0 } }));
  };

  const enableTubeTypeRow = async (t) => {
    await dispatch(updateTubeType({ id: t.id, data: { is_active: 1 } }));
  };

  const toggleTubeType = (tubeTypeId) => {
    if (!managedSampleTypeId) return;
    const exists = managedTubeTypeIds.includes(tubeTypeId);
    const next = exists
      ? managedTubeTypeIds.filter((id) => id !== tubeTypeId)
      : [...managedTubeTypeIds, tubeTypeId];

    dispatch(saveSampleTypeTubeTypes({ sampleTypeId: managedSampleTypeId, tubeTypeIds: next }));
  };

  const addParameter = async () => {
    if (!managedSampleTypeId) return;
    const name = String(parameterForm.name || "").trim();
    if (!name) return;

    await dispatch(
      createSampleTypeParameter({
        sampleTypeId: managedSampleTypeId,
        data: {
          name,
          data_type: parameterForm.data_type,
          unit: parameterForm.unit || null,
          is_active: 1,
        },
      })
    );

    setParameterForm({ name: "", data_type: "numeric", unit: "" });
  };

  const startEditParameter = (p) => {
    setEditingParameterId(p.id);
    setParameterEditDraft({
      name: p.name || "",
      data_type: p.data_type || "numeric",
      unit: p.unit || "",
      is_active: p.is_active === 0 ? false : true,
    });
  };

  const cancelEditParameter = () => {
    setEditingParameterId(null);
    setParameterEditDraft({ name: "", data_type: "numeric", unit: "", is_active: true });
  };

  const saveParameter = async () => {
    if (!editingParameterId) return;
    const name = String(parameterEditDraft.name || "").trim();
    if (!name) return;

    await dispatch(
      updateSampleTypeParameter({
        parameterId: editingParameterId,
        data: {
          name,
          data_type: parameterEditDraft.data_type,
          unit: parameterEditDraft.unit || null,
          is_active: parameterEditDraft.is_active ? 1 : 0,
        },
      })
    );
    cancelEditParameter();
  };

  const deleteParameter = async (p) => {
    const ok = await confirmAsync({
      title: "Delete parameter",
      message:
        "Delete this parameter? This will delete its reference ranges too. This cannot be undone.",
      confirmText: "Delete",
      confirmTone: "danger",
    });
    if (!ok) return;
    await dispatch(deleteSampleTypeParameter(p.id));
    if (String(managedParameterId) === String(p.id)) setManagedParameterId(null);
  };

  const disableParameterRow = async (p) => {
    await dispatch(updateSampleTypeParameter({ parameterId: p.id, data: { is_active: 0 } }));
  };

  const enableParameterRow = async (p) => {
    await dispatch(updateSampleTypeParameter({ parameterId: p.id, data: { is_active: 1 } }));
  };

  const openRangesForParameter = (p) => {
    setManagedParameterId(p.id);
    setSampleTypeSection("ranges");
    dispatch(fetchTemplateRanges(p.id));
  };

  const setRangesParameter = (parameterId) => {
    setManagedParameterId(parameterId);
    if (parameterId) dispatch(fetchTemplateRanges(parameterId));
  };

  const addRange = async () => {
    if (!managedParameterId) return;

    const payload = showTextRangeColumns
      ? {
          gender: rangeForm.gender,
          pregnancy_status: rangeForm.pregnancy_status,
          age_unit: rangeForm.age_unit || "years",
          min_age: rangeForm.min_age,
          max_age: rangeForm.max_age,
          unit: rangeForm.unit || null,
          text_range: rangeForm.text_range || null,
          remarks: rangeForm.remarks || null,
          conditions: null,
          min_value: null,
          max_value: null,
          critical_low: null,
          critical_high: null,
        }
      : {
          gender: rangeForm.gender,
          pregnancy_status: rangeForm.pregnancy_status,
          age_unit: rangeForm.age_unit || "years",
          min_age: rangeForm.min_age,
          max_age: rangeForm.max_age,
          unit: rangeForm.unit || null,
          min_value: rangeForm.min_value,
          max_value: rangeForm.max_value,
          critical_low: rangeForm.critical_low,
          critical_high: rangeForm.critical_high,
          text_range: null,
          remarks: rangeForm.remarks || null,
          conditions: null,
        };

        console.log("Adding range with payload:", payload);

    await dispatch(
      createTemplateRange({
        parameterId: managedParameterId,
        data: payload,
      })
    );
    if (managedParameterId) {
      await dispatch(fetchTemplateRanges(managedParameterId));
    }

    setRangeForm({
      gender: "both",
      pregnancy_status: "both",
      age_unit: "years",
      min_age: "",
      max_age: "",
      unit: "",
      min_value: "",
      max_value: "",
      critical_low: "",
      critical_high: "",
      text_range: "",
      remarks: "",
    });
  };

  const removeRange = async (r) => {
    const ok = await confirmAsync({
      title: "Delete reference range",
      message: "Delete this reference range? This cannot be undone.",
      confirmText: "Delete",
      confirmTone: "danger",
    });
    if (!ok) return;
    await dispatch(deleteTemplateRange(r.id));
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader
          title="Laboratory Configuration (Master Data)"
          subtitle="Prepare reusable building blocks once: categories and sample type templates (tube types, parameters, and reference ranges)."
        />
        <div className="p-4">
          <Tabs
            tabs={[
              { id: "categories", label: "Test Categories", Icon: Tag },
              { id: "sampleTypes", label: "Sample Types", Icon: Droplets },
              { id: "tubeTypes", label: "Tube Types" },
            ]}
            activeId={tab}
            onChange={(id) => {
              setTab(id);
              backToSampleTypes();
            }}
          />
        </div>
      </Card>

      {tab === "categories" ? (
        <Card>
          <CardHeader
            title="Test Categories"
            subtitle="Simple CRUD: name, description, active status"
            right={
              <PrimaryButton type="button" onClick={openCreateCategory}>
                <Plus className="h-4 w-4" />
                New
              </PrimaryButton>
            }
          />

          <div className="p-4">
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <div className="col-span-3">Category</div>
                <div className="col-span-3">Status</div>
                <div className="col-span-5">Description</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>
              <div className="divide-y divide-slate-200">
                {categoriesSorted.length === 0 ? (
                  <div className="p-4 text-sm text-slate-600">No categories yet.</div>
                ) : (
                  categoriesSorted.map((c) => {
                    const active = c.is_active === 0 ? false : true;
                    return (
                      <div key={c.id} className="grid grid-cols-12 gap-2 px-3 py-3">
                        <div className="col-span-3">
                          <div className="text-sm font-medium text-slate-900">{c.name}</div>
                        </div>
                        <div className="col-span-3 flex items-center">
                          <Badge tone={active ? "success" : "neutral"}>
                            {active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="col-span-5">
                          <div className="text-sm text-slate-700 line-clamp-2">{c.description || "—"}</div>
                        </div>
                        <div className="col-span-1 flex items-center justify-end gap-2">
                          <SecondaryButton
                            type="button"
                            className="h-9 px-3"
                            onClick={() => openEditCategory(c)}
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </SecondaryButton>
                          {active ? (
                            <DangerButton
                              type="button"
                              className="h-9 px-3"
                              onClick={() => disableCategory(c)}
                            >
                              <X className="h-4 w-4" />
                              Disable
                            </DangerButton>
                          ) : (
                            <SecondaryButton
                              type="button"
                              className="h-9 px-3"
                              onClick={() => enableCategory(c)}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              Enable
                            </SecondaryButton>
                          )}
                          <DangerButton
                            type="button"
                            className="h-9 px-3"
                            onClick={() => deleteCategoryRow(c)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </DangerButton>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {loading ? <div className="mt-3 text-xs text-slate-500">Loading…</div> : null}
          </div>

          <Modal
            open={categoryModalOpen}
            title={categoryEditingId ? "Edit Category" : "Create Category"}
            onClose={() => setCategoryModalOpen(false)}
          >
            <div className="space-y-4">
              <Field label="Name (required)">
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Hematology"
                />
              </Field>
              <Field label="Description (optional)">
                <TextArea
                  value={categoryForm.description}
                  onChange={(e) =>
                    setCategoryForm((p) => ({ ...p, description: e.target.value }))
                  }
                  placeholder="Short description"
                />
              </Field>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={categoryForm.is_active}
                  onChange={(e) =>
                    setCategoryForm((p) => ({ ...p, is_active: e.target.checked }))
                  }
                />
                Active
              </label>

              <div className="flex items-center justify-end gap-2">
                <SecondaryButton type="button" onClick={() => setCategoryModalOpen(false)}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="button" onClick={saveCategory} disabled={!String(categoryForm.name || "").trim()}>
                  <Save className="h-4 w-4" />
                  Save
                </PrimaryButton>
              </div>
            </div>
          </Modal>
        </Card>
      ) : null}

      {tab === "sampleTypes" ? (
        managedSampleType ? (
          <Card>
            <CardHeader
              title={`Sample Type: ${managedSampleType.name}`}
              subtitle="Overview, tube types, parameters, and reference ranges"
              right={
                <SecondaryButton type="button" onClick={backToSampleTypes}>
                  Back
                </SecondaryButton>
              }
            />

            <div className="p-4 space-y-4">
              <Tabs
                tabs={[
                  { id: "overview", label: "Overview" },
                  { id: "tubes", label: "Tube Types" },
                  { id: "parameters", label: "Parameters" },
                  { id: "ranges", label: "Reference Ranges" },
                ]}
                activeId={sampleTypeSection}
                onChange={setSampleTypeSection}
              />

              {sampleTypeSection === "overview" ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div className="space-y-3">
                    <Field label="Name">
                      <Input
                        value={sampleTypeDraft.name}
                        onChange={(e) =>
                          setSampleTypeDraft((p) => ({ ...p, name: e.target.value }))
                        }
                      />
                    </Field>
                    <Field label="Description">
                      <TextArea
                        value={sampleTypeDraft.description}
                        onChange={(e) =>
                          setSampleTypeDraft((p) => ({
                            ...p,
                            description: e.target.value,
                          }))
                        }
                      />
                    </Field>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={sampleTypeDraft.is_active}
                        onChange={(e) =>
                          setSampleTypeDraft((p) => ({
                            ...p,
                            is_active: e.target.checked,
                          }))
                        }
                      />
                      Active
                    </label>
                    <div className="flex items-center gap-2">
                      <PrimaryButton type="button" onClick={saveSampleTypeOverview}>
                        <Save className="h-4 w-4" />
                        Save
                      </PrimaryButton>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-sm font-semibold text-slate-900">Summary</div>
                    <div className="mt-2 text-sm text-slate-700">
                      Tube types linked: <span className="font-semibold">{managedTubeTypeIds.length}</span>
                    </div>
                    <div className="mt-1 text-sm text-slate-700">
                      Parameters: <span className="font-semibold">{managedParameters.length}</span>
                    </div>
                    <div className="mt-1 text-sm text-slate-700">
                      Status: <span className="font-semibold">{managedSampleType.is_active === 0 ? "Inactive" : "Active"}</span>
                    </div>
                  </div>
                </div>
              ) : null}

              {sampleTypeSection === "tubes" ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900">Compatible tube types</div>
                      <SecondaryButton
                        type="button"
                        className="h-9 px-3"
                        onClick={() => openCreateTubeType({ linkToSampleTypeId: managedSampleTypeId })}
                      >
                        <Plus className="h-4 w-4" />
                        New tube type
                      </SecondaryButton>
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                      Select which tube types are valid for this sample type.
                    </div>
                    <div className="mt-3 space-y-2">
                      {tubeTypesSorted.length === 0 ? (
                        <div className="text-sm text-slate-600">No tube types found.</div>
                      ) : (
                        tubeTypesSorted.map((t) => (
                          <label key={t.id} className="flex items-center gap-2 text-sm text-slate-800">
                            <input
                              type="checkbox"
                              checked={managedTubeTypeIds.includes(t.id)}
                              onChange={() => toggleTubeType(t.id)}
                            />
                            {t.name}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-sm font-semibold text-slate-900">Currently selected</div>
                    <div className="mt-2 text-sm text-slate-700">
                      {managedTubeTypeIds.length === 0
                        ? "None"
                        : managedTubeTypeIds
                            .map((id) => tubeTypes.find((t) => t.id === id)?.name)
                            .filter(Boolean)
                            .join(", ")}
                    </div>
                  </div>
                </div>
              ) : null}

              {sampleTypeSection === "parameters" ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-sm font-semibold text-slate-900">Add parameter</div>
                    <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-4">
                      <Field label="Parameter name">
                        <Input
                          value={parameterForm.name}
                          onChange={(e) =>
                            setParameterForm((p) => ({ ...p, name: e.target.value }))
                          }
                          placeholder="e.g. Glucose"
                        />
                      </Field>
                      <Field label="Data type">
                        <Select
                          value={parameterForm.data_type}
                          onChange={(e) =>
                            setParameterForm((p) => ({ ...p, data_type: e.target.value }))
                          }
                        >
                          {DATA_TYPES.map((d) => (
                            <option key={d.value} value={d.value}>
                              {d.label}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Field label="Unit (optional)">
                        <Input
                          value={parameterForm.unit}
                          onChange={(e) =>
                            setParameterForm((p) => ({ ...p, unit: e.target.value }))
                          }
                          placeholder="e.g. mg/dL"
                        />
                      </Field>
                      <div className="flex items-end">
                        <PrimaryButton
                          type="button"
                          onClick={addParameter}
                          disabled={!String(parameterForm.name || "").trim()}
                          className="w-full"
                        >
                          <Plus className="h-4 w-4" />
                          Save
                        </PrimaryButton>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                      <div className="col-span-3">Parameter</div>
                      <div className="col-span-2">Data Type</div>
                      <div className="col-span-1">Unit</div>
                      <div className="col-span-2">Status</div>
                      <div className="col-span-4 text-right">Actions</div>
                    </div>

                    <div className="divide-y divide-slate-200">
                      {managedParameters.length === 0 ? (
                        <div className="p-4 text-sm text-slate-600">No parameters yet.</div>
                      ) : (
                        managedParameters.map((p) => {
                          const editing = editingParameterId === p.id;
                          const active = p.is_active === 0 ? false : true;

                          return (
                            <div key={p.id} className="grid grid-cols-12 gap-2 px-3 py-3">
                              <div className="col-span-3">
                                {editing ? (
                                  <Input
                                    value={parameterEditDraft.name}
                                    onChange={(e) =>
                                      setParameterEditDraft((d) => ({
                                        ...d,
                                        name: e.target.value,
                                      }))
                                    }
                                  />
                                ) : (
                                  <div className="text-sm font-medium text-slate-900">{p.name}</div>
                                )}
                              </div>
                              <div className="col-span-2">
                                {editing ? (
                                  <Select
                                    value={parameterEditDraft.data_type}
                                    onChange={(e) =>
                                      setParameterEditDraft((d) => ({
                                        ...d,
                                        data_type: e.target.value,
                                      }))
                                    }
                                  >
                                    {DATA_TYPES.map((d) => (
                                      <option key={d.value} value={d.value}>
                                        {d.label}
                                      </option>
                                    ))}
                                  </Select>
                                ) : (
                                  <div className="text-sm text-slate-700">{p.data_type || "numeric"}</div>
                                )}
                              </div>
                              <div className="col-span-1">
                                {editing ? (
                                  <Input
                                    value={parameterEditDraft.unit}
                                    onChange={(e) =>
                                      setParameterEditDraft((d) => ({
                                        ...d,
                                        unit: e.target.value,
                                      }))
                                    }
                                    placeholder="(optional)"
                                  />
                                ) : (
                                  <div className="text-sm text-slate-700">{p.unit || "—"}</div>
                                )}
                              </div>
                              <div className="col-span-2 flex items-center">
                                {editing ? (
                                  <label className="flex items-center gap-2 text-sm text-slate-700">
                                    <input
                                      type="checkbox"
                                      checked={parameterEditDraft.is_active}
                                      onChange={(e) =>
                                        setParameterEditDraft((d) => ({
                                          ...d,
                                          is_active: e.target.checked,
                                        }))
                                      }
                                    />
                                    Active
                                  </label>
                                ) : (
                                  <Badge tone={active ? "success" : "neutral"}>
                                    {active ? "Active" : "Inactive"}
                                  </Badge>
                                )}
                              </div>
                              <div className="col-span-4 flex items-center justify-end gap-2">
                                {editing ? (
                                  <>
                                    <PrimaryButton
                                      type="button"
                                      className="h-9 px-3"
                                      onClick={saveParameter}
                                      disabled={!String(parameterEditDraft.name || "").trim()}
                                    >
                                      <Save className="h-4 w-4" />
                                      Save
                                    </PrimaryButton>
                                    <SecondaryButton
                                      type="button"
                                      className="h-9 px-3"
                                      onClick={cancelEditParameter}
                                    >
                                      <X className="h-4 w-4" />
                                      Cancel
                                    </SecondaryButton>
                                  </>
                                ) : (
                                  <>
                                    <SecondaryButton
                                      type="button"
                                      className="h-9 px-3"
                                      onClick={() => {
                                        openRangesForParameter(p);
                                      }}
                                    >
                                      Manage
                                    </SecondaryButton>
                                    <SecondaryButton
                                      type="button"
                                      className="h-9 px-3"
                                      onClick={() => startEditParameter(p)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                      Edit
                                    </SecondaryButton>
                                    {active ? (
                                      <DangerButton
                                        type="button"
                                        className="h-9 px-3"
                                        onClick={() => disableParameterRow(p)}
                                      >
                                        <X className="h-4 w-4" />
                                        Disable
                                      </DangerButton>
                                    ) : (
                                      <SecondaryButton
                                        type="button"
                                        className="h-9 px-3"
                                        onClick={() => enableParameterRow(p)}
                                      >
                                        <CheckCircle2 className="h-4 w-4" />
                                        Enable
                                      </SecondaryButton>
                                    )}
                                    <DangerButton
                                      type="button"
                                      className="h-9 px-3"
                                      onClick={() => deleteParameter(p)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      Delete
                                    </DangerButton>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              {sampleTypeSection === "ranges" ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-sm font-semibold text-slate-900">Select parameter</div>
                    <div className="mt-2">
                      <Select
                        value={managedParameterId || ""}
                        onChange={(e) => setRangesParameter(e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">Choose…</option>
                        {managedParameters.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </Select>
                    </div>
                    {selectedParam ? (
                      <div className="mt-2 text-sm text-slate-700">
                        Parameter: <span className="font-semibold">{selectedParam.name}</span>
                      </div>
                    ) : null}
                  </div>

                  {managedParameterId ? (
                    <>
                      <div className="overflow-hidden rounded-lg border border-slate-200">
                        <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                          <div className="col-span-2">Gender</div>
                          <div className="col-span-2">Pregnancy</div>
                          <div className="col-span-1">Age</div>
                          <div className="col-span-2">{showTextRangeColumns ? "Range" : "Min/Max"}</div>
                          <div className="col-span-2">{showTextRangeColumns ? "Remarks" : "Critical"}</div>
                          <div className="col-span-1">Unit</div>
                          <div className="col-span-2 text-right">Actions</div>
                        </div>
                        <div className="divide-y divide-slate-200">
                          {selectedRanges.length === 0 ? (
                            <div className="p-4 text-sm text-slate-600">No ranges yet.</div>
                          ) : (
                            selectedRanges.map((r) => {
                              const isTextRow =
                                showTextRangeColumns ||
                                String(r?.text_range ?? "").trim().length > 0 ||
                                String(r?.remarks ?? "").trim().length > 0;

                              return (
                                <div key={r.id} className="grid grid-cols-12 gap-2 px-3 py-3 text-sm text-slate-800">
                                  <div className="col-span-2">
                                    {r.gender === "both" ? "Any" : r.gender}
                                  </div>
                                  <div className="col-span-2">
                                    {r.pregnancy_status === "both" ? "Any" : r.pregnancy_status}
                                  </div>
                                  <div className="col-span-1">
                                    {r.min_age ?? "—"}–{r.max_age ?? "—"} {r.age_unit || "years"}
                                  </div>
                                  <div className="col-span-2">
                                    {isTextRow
                                      ? (r.text_range || "—")
                                      : `${r.min_value ?? "—"}–${r.max_value ?? "—"}`}
                                  </div>
                                  <div className="col-span-2">
                                    {isTextRow
                                      ? (r.remarks ?? "—")
                                      : `${r.critical_low ?? "—"}–${r.critical_high ?? "—"}`}
                                  </div>
                                  <div className="col-span-1">{r.unit || "—"}</div>
                                  <div className="col-span-2 flex justify-end">
                                    <DangerButton
                                      type="button"
                                      className="h-9 px-3"
                                      onClick={() => removeRange(r)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </DangerButton>
                                  </div>
                                  {r.remarks ? (
                                    <div className="col-span-12 pt-1 text-xs text-slate-600">
                                      Remarks: {r.remarks}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="text-sm font-semibold text-slate-900">Add reference range</div>
                        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-4">
                          <Field label="Gender">
                            <Select
                              value={rangeForm.gender}
                              onChange={(e) => setRangeForm((p) => ({ ...p, gender: e.target.value }))}
                            >
                              <option value="both">Any</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                            </Select>
                          </Field>
                          <Field label="Pregnancy">
                            <Select
                              value={rangeForm.pregnancy_status}
                              onChange={(e) =>
                                setRangeForm((p) => ({ ...p, pregnancy_status: e.target.value }))
                              }
                            >
                              <option value="both">Any</option>
                              <option value="pregnant">Pregnant</option>
                              <option value="not_pregnant">Not Pregnant</option>
                            </Select>
                          </Field>
                          <Field label="Min age">
                            <Input
                              value={rangeForm.min_age}
                              onChange={(e) => setRangeForm((p) => ({ ...p, min_age: e.target.value }))}
                              placeholder="e.g. 0"
                            />
                          </Field>
                          <Field label="Max age">
                            <Input
                              value={rangeForm.max_age}
                              onChange={(e) => setRangeForm((p) => ({ ...p, max_age: e.target.value }))}
                              placeholder="e.g. 120"
                            />
                          </Field>
                          <Field label="Age unit">
                            <Select
                              value={rangeForm.age_unit}
                              onChange={(e) => setRangeForm((p) => ({ ...p, age_unit: e.target.value }))}
                            >
                              <option value="years">Years</option>
                              <option value="months">Months</option>
                              <option value="weeks">Weeks</option>
                              <option value="days">Days</option>
                            </Select>
                          </Field>
                        </div>

                        {showTextRangeColumns ? (
                          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
                            <Field label="Range">
                              <Input
                                value={rangeForm.text_range}
                                onChange={(e) =>
                                  setRangeForm((p) => ({ ...p, text_range: e.target.value }))
                                }
                                placeholder="e.g. Negative, Trace, 1+"
                              />
                            </Field>
                            <Field label="Remarks">
                              <TextArea
                                value={rangeForm.remarks}
                                onChange={(e) =>
                                  setRangeForm((p) => ({ ...p, remarks: e.target.value }))
                                }
                                placeholder="Optional remarks"
                              />
                            </Field>
                            <Field label="Unit">
                              <Input
                                value={rangeForm.unit}
                                onChange={(e) => setRangeForm((p) => ({ ...p, unit: e.target.value }))}
                                placeholder="e.g. mg/dL"
                              />
                            </Field>
                          </div>
                        ) : (
                          <>
                            <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-5">
                              <Field label="Min value">
                                <Input
                                  value={rangeForm.min_value}
                                  onChange={(e) =>
                                    setRangeForm((p) => ({ ...p, min_value: e.target.value }))
                                  }
                                  placeholder="e.g. 70"
                                />
                              </Field>
                              <Field label="Max value">
                                <Input
                                  value={rangeForm.max_value}
                                  onChange={(e) =>
                                    setRangeForm((p) => ({ ...p, max_value: e.target.value }))
                                  }
                                  placeholder="e.g. 110"
                                />
                              </Field>
                              <Field label="Critical low (optional)">
                                <Input
                                  value={rangeForm.critical_low}
                                  onChange={(e) =>
                                    setRangeForm((p) => ({ ...p, critical_low: e.target.value }))
                                  }
                                  placeholder=""
                                />
                              </Field>
                              <Field label="Critical high (optional)">
                                <Input
                                  value={rangeForm.critical_high}
                                  onChange={(e) =>
                                    setRangeForm((p) => ({ ...p, critical_high: e.target.value }))
                                  }
                                  placeholder=""
                                />
                              </Field>
                              <Field label="Unit">
                                <Input
                                  value={rangeForm.unit}
                                  onChange={(e) => setRangeForm((p) => ({ ...p, unit: e.target.value }))}
                                  placeholder="e.g. mg/dL"
                                />
                              </Field>
                            </div>

                            <div className="mt-3">
                              <Field label="Remarks">
                                <TextArea
                                  value={rangeForm.remarks}
                                  onChange={(e) =>
                                    setRangeForm((p) => ({ ...p, remarks: e.target.value }))
                                  }
                                  placeholder="Optional remarks"
                                />
                              </Field>
                            </div>
                          </>
                        )}

                        <div className="mt-3 flex items-center justify-end">
                          <PrimaryButton type="button" onClick={addRange}>
                            <Plus className="h-4 w-4" />
                            Add
                          </PrimaryButton>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-slate-600">Select a parameter to manage ranges.</div>
                  )}
                </div>
              ) : null}
            </div>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title="Sample Types"
              subtitle="Each sample type owns tube types, default parameters, and default reference ranges"
              right={
                <PrimaryButton type="button" onClick={openCreateSampleType}>
                  <Plus className="h-4 w-4" />
                  New
                </PrimaryButton>
              }
            />

            <div className="p-4">
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                  <div className="col-span-3">Sample Type</div>
                  <div className="col-span-2">Tube Types</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Parameters</div>
                  <div className="col-span-3 text-right">Actions</div>
                </div>

                <div className="divide-y divide-slate-200">
                  {sampleTypesSorted.length === 0 ? (
                    <div className="p-4 text-sm text-slate-600">No sample types yet.</div>
                  ) : (
                    sampleTypesSorted.map((st) => {
                      const tubeIds = config.tubeTypeIdsBySampleTypeId[st.id] || [];
                      const tubeNames = tubeIds
                        .map((id) => tubeTypes.find((t) => t.id === id)?.name)
                        .filter(Boolean);
                      const params = config.parametersBySampleTypeId[st.id] || [];
                      const active = st.is_active === 0 ? false : true;

                      return (
                        <div key={st.id} className="grid grid-cols-12 gap-2 px-3 py-3">
                          <div className="col-span-3">
                            <div className="text-sm font-medium text-slate-900">{st.name}</div>
                            {st.description ? (
                              <div className="mt-1 text-xs text-slate-600 line-clamp-2">{st.description}</div>
                            ) : null}
                          </div>
                          <div className="col-span-2">
                            <div className="text-sm text-slate-700 line-clamp-2">
                              {tubeNames.length === 0 ? "—" : tubeNames.join(", ")}
                            </div>
                          </div>
                          <div className="col-span-2 flex items-center">
                            <Badge tone={active ? "success" : "neutral"}>
                              {active ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <div className="col-span-2 flex items-center">
                            <div className="text-sm text-slate-800">{params.length}</div>
                          </div>
                          <div className="col-span-3 flex items-center justify-end gap-2">
                            <SecondaryButton
                              type="button"
                              className="h-9 px-3"
                              onClick={() => openManageSampleType(st)}
                            >
                              Manage
                            </SecondaryButton>
                            {active ? (
                              <DangerButton
                                type="button"
                                className="h-9 px-3"
                                onClick={() => disableSampleTypeRow(st)}
                              >
                                <X className="h-4 w-4" />
                                Disable
                              </DangerButton>
                            ) : (
                              <SecondaryButton
                                type="button"
                                className="h-9 px-3"
                                onClick={() => enableSampleTypeRow(st)}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                                Enable
                              </SecondaryButton>
                            )}
                            <DangerButton
                              type="button"
                              className="h-9 px-3"
                              onClick={() => deleteSampleTypeRow(st)}
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </DangerButton>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {loading ? <div className="mt-3 text-xs text-slate-500">Loading…</div> : null}
            </div>
          </Card>
        )
      ) : null}

      {tab === "tubeTypes" ? (
        <Card>
          <CardHeader
            title="Tube Types"
            subtitle="Simple CRUD: name, description, active status"
            right={
              <PrimaryButton type="button" onClick={() => openCreateTubeType()}>
                <Plus className="h-4 w-4" />
                New
              </PrimaryButton>
            }
          />

          <div className="p-4">
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <div className="col-span-3">Tube Type</div>
                <div className="col-span-5">Description</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-3 text-right">Actions</div>
              </div>
              <div className="divide-y divide-slate-200">
                {tubeTypesSorted.length === 0 ? (
                  <div className="p-4 text-sm text-slate-600">No tube types yet.</div>
                ) : (
                  tubeTypesSorted.map((t) => {
                    const active = t.is_active === 0 ? false : true;
                    return (
                      <div key={t.id} className="grid grid-cols-12 gap-2 px-3 py-3">
                        <div className="col-span-3">
                          <div className="text-sm font-medium text-slate-900">{t.name}</div>
                        </div>
                        <div className="col-span-5">
                          <div className="text-sm text-slate-700 line-clamp-2">{t.description || "—"}</div>
                        </div>
                        <div className="col-span-1 flex items-center">
                          <Badge tone={active ? "success" : "neutral"}>{active ? "Active" : "Inactive"}</Badge>
                        </div>
                        <div className="col-span-3 flex items-center justify-end gap-2">
                          <SecondaryButton
                            type="button"
                            className="h-9 px-3"
                            onClick={() => openEditTubeType(t)}
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </SecondaryButton>
                          {active ? (
                            <DangerButton
                              type="button"
                              className="h-9 px-3"
                              onClick={() => disableTubeTypeRow(t)}
                            >
                              <X className="h-4 w-4" />
                              Disable
                            </DangerButton>
                          ) : (
                            <SecondaryButton
                              type="button"
                              className="h-9 px-3"
                              onClick={() => enableTubeTypeRow(t)}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              Enable
                            </SecondaryButton>
                          )}
                          <DangerButton
                            type="button"
                            className="h-9 px-3"
                            onClick={() => deleteTubeTypeRow(t)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </DangerButton>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      <Modal
        open={tubeTypeModalOpen}
        title={tubeTypeEditingId ? "Edit Tube Type" : "Create Tube Type"}
        onClose={() => {
          setTubeTypeModalOpen(false);
          setTubeTypeCreateLinkSampleTypeId(null);
        }}
      >
        <div className="space-y-4">
          <Field label="Name (required)">
            <Input
              value={tubeTypeForm.name}
              onChange={(e) => setTubeTypeForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. EDTA"
            />
          </Field>
          <Field label="Description (optional)">
            <TextArea
              value={tubeTypeForm.description}
              onChange={(e) => setTubeTypeForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Short description"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={tubeTypeForm.is_active}
              onChange={(e) => setTubeTypeForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
            Active
          </label>

          <div className="flex items-center justify-end gap-2">
            <SecondaryButton
              type="button"
              onClick={() => {
                setTubeTypeModalOpen(false);
                setTubeTypeCreateLinkSampleTypeId(null);
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={saveTubeType}
              disabled={!String(tubeTypeForm.name || "").trim()}
            >
              <Save className="h-4 w-4" />
              Save
            </PrimaryButton>
          </div>
        </div>
      </Modal>
      <Modal
        open={sampleTypeCreateOpen}
        title="Create Sample Type"
        onClose={() => setSampleTypeCreateOpen(false)}
      >
        <div className="space-y-4">
          <Field label="Name (required)">
            <Input
              value={sampleTypeCreateForm.name}
              onChange={(e) =>
                setSampleTypeCreateForm((p) => ({ ...p, name: e.target.value }))
              }
              placeholder="e.g. Serum"
            />
          </Field>
          <Field label="Description (optional)">
            <TextArea
              value={sampleTypeCreateForm.description}
              onChange={(e) =>
                setSampleTypeCreateForm((p) => ({ ...p, description: e.target.value }))
              }
              placeholder="Short description"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={sampleTypeCreateForm.is_active}
              onChange={(e) =>
                setSampleTypeCreateForm((p) => ({ ...p, is_active: e.target.checked }))
              }
            />
            Active
          </label>

          <div className="flex items-center justify-end gap-2">
            <SecondaryButton type="button" onClick={() => setSampleTypeCreateOpen(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={saveNewSampleType}
              disabled={!String(sampleTypeCreateForm.name || "").trim()}
            >
              <Save className="h-4 w-4" />
              Save
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      {confirmDialog.open ? (
        <div className="fixed inset-0 z-[60]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            aria-label="Close"
            onClick={() => closeConfirmDialog(true)}
          />
          <div className="absolute left-1/2 top-1/2 w-[min(520px,calc(100%-2rem))] -translate-x-1/2 -translate-y-1/2">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 p-5">
                <div className="text-sm font-semibold text-slate-900">{confirmDialog.title}</div>
                <div className="mt-1 text-xs text-slate-500">Please confirm the action</div>
              </div>
              <div className="p-5">
                <div className="text-sm text-slate-700">{confirmDialog.message}</div>
                <div className="mt-4 flex items-center justify-end gap-2">
                  <SecondaryButton
                    type="button"
                    onClick={() => closeConfirmDialog(true)}
                    disabled={confirmDialogBusy}
                  >
                    Cancel
                  </SecondaryButton>
                  <DangerButton
                    type="button"
                    disabled={confirmDialogBusy}
                    onClick={async () => {
                      const fn = confirmDialogActionRef.current;
                      if (!fn) {
                        closeConfirmDialog(true);
                        return;
                      }
                      try {
                        setConfirmDialogBusy(true);
                        await fn();
                        closeConfirmDialog(false);
                      } finally {
                        setConfirmDialogBusy(false);
                      }
                    }}
                  >
                    {confirmDialog.confirmText}
                  </DangerButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LaboratoryConfiguration;
